/**
 * Качество видео.
 *
 * ── Почему регулятор именно такой ───────────────────────────────────
 *
 * Кодек сменить на лету нельзя — дорожку пришлось бы публиковать заново,
 * и картинка оборвалась бы у всех. А вот потолок битрейта и приоритет
 * деградации меняются через `RTCRtpSender.setParameters`: это не трогает
 * саму дорожку и не требует пересогласования. Ровно так же, как у звука.
 *
 * Поэтому управляем тем, что человек действительно ощущает как качество,
 * и тем, что можно поменять посреди разговора без последствий.
 *
 * Числа — это **потолки**, а не запросы. Контроль перегрузки WebRTC сам
 * держится ниже, когда канал не тянет, и сам поднимается обратно.
 */

export type VideoMode = 'saver' | 'auto' | 'high';

export const VIDEO_MODE_LABEL: Record<VideoMode, string> = {
  saver: 'Экономный',
  auto: 'Авто',
  high: 'Высокое',
};

export const VIDEO_MODE_NOTE: Record<VideoMode, string> = {
  saver: 'Съёмка 720p, до 0.6 Мбит/с — для мобильного интернета и слабых ноутбуков',
  auto: 'Съёмка 720p, до 2.5 Мбит/с — качество подстраивается под связь',
  high: 'Съёмка 1080p, до 4 Мбит/с — чёткость важнее плавности, процессор нагружен сильнее',
};

export interface VideoProfile {
  /**
   * Общий потолок на весь исходящий видеопоток, бит/с.
   *
   * Именно **общий**, а не на слой. У VP9 и AV1 слой один (SVC считает
   * битрейт сразу за все три), а у H.264 и VP8 их три штуки, и сумма
   * как раз и уходит в сеть. Поэтому бюджет надо делить — см.
   * `distributeBudget`.
   */
  maxBitrate: number;
  /**
   * Потолок для демонстрации экрана, бит/с.
   *
   * Выше, чем у камеры: экран — это 1080p и больше, и на нём обычно
   * текст, которому нужна разборчивость. Но режимом всё равно
   * управляется — иначе выбравший «Экономный» на мобильном интернете
   * получал бы четыре мегабита, как только начнёт показывать экран.
   */
  screenShareBitrate: number;
  /**
   * Чем жертвовать при показе экрана.
   *
   * ── Почему не так же, как у камеры ──────────────────────────────
   *
   * Здесь долго стоял `maintain-resolution`: рассуждение было в том, что
   * текст, потерявший разборчивость, бесполезен на любой частоте кадров.
   * Рассуждение верное для неподвижного слайда и неверное для всего
   * остального. Как только на экране что-то движется — прокрутка
   * документа, курсор, переход между слайдами, — кодировщику не хватает
   * битрейта, и он честно делает то, что просили: держит 1080p и роняет
   * частоту до нескольких кадров. Со стороны это выглядит как рывки.
   *
   * `balanced` даёт кодировщику право на время движения смягчить
   * картинку и вернуть чёткость, как только движение кончилось. На
   * неподвижном слайде разницы нет вовсе — деградация просто не нужна,
   * — а прокрутка перестаёт дёргаться.
   */
  screenShareDegradation: RTCDegradationPreference;
  /**
   * Разрешение съёмки камерой.
   *
   * 1080p только в режиме «Высокое», и это осознанно. В сетке на четверых
   * плитка занимает около 400 px — отправлять в неё 1080p значит впятеро
   * нагрузить кодировщик ради пикселей, которых никто не увидит. Именно
   * перегрузка кодировщика когда-то давала подтормаживание уже на трёх
   * участниках. 1080p оправдан в разговоре один на один во весь экран
   * и когда камерой показывают документ или доску.
   */
  resolution: { width: number; height: number };
  /**
   * Чем жертвовать, когда не хватает канала или процессора.
   *
   * `maintain-resolution` бережёт чёткость и роняет частоту кадров —
   * это осознанный выбор для показа мелких деталей.
   * `maintain-framerate` наоборот: движение важнее, картинка мельчает.
   */
  degradation: RTCDegradationPreference;
}

const HD = { width: 1280, height: 720 };
const FULL_HD = { width: 1920, height: 1080 };

const PROFILES: Record<VideoMode, VideoProfile> = {
  saver: {
    maxBitrate: 600_000,
    screenShareBitrate: 1_500_000,
    resolution: HD,
    degradation: 'maintain-framerate',
    screenShareDegradation: 'maintain-framerate',
  },
  auto: {
    maxBitrate: 2_500_000,
    /*
     * Четыре мегабита, а не три.
     *
     * Три упирались ровно в 1080p при движении: на неподвижном слайде
     * хватало с запасом, а на прокрутке кодировщику приходилось резать.
     * Канал сервера гигабитный, и лишний мегабит на одного показывающего
     * — несопоставимо меньшая цена, чем дёргающийся доклад.
     */
    screenShareBitrate: 4_000_000,
    resolution: HD,
    degradation: 'balanced',
    screenShareDegradation: 'balanced',
  },
  high: {
    maxBitrate: 4_000_000,
    screenShareBitrate: 6_000_000,
    resolution: FULL_HD,
    degradation: 'maintain-resolution',
    // Даже в режиме «чёткость важнее» показ экрана остаётся
    // сбалансированным: рывки на прокрутке замечают все, а разницу
    // между 1080p и 900p во время движения — никто.
    screenShareDegradation: 'balanced',
  },
};

export const videoProfileFor = (mode: VideoMode): VideoProfile => PROFILES[mode];

/** Человекочитаемый потолок: «2.5 Мбит/с», «4 Мбит/с» — без лишнего нуля. */
export const formatVideoBitrate = (bps: number) =>
  `${Number((bps / 1e6).toFixed(1))} Мбит/с`;

/**
 * Делит общий бюджет между слоями simulcast.
 *
 * Раньше потолок ставился на каждый слой целиком — и три слоя H.264
 * получали по 2.5 Мбит/с каждый, то есть до 7.5 Мбит/с в сеть вместо
 * обещанных 2.5. На телефоне это было видно прямо в панели: «битрейт
 * видео 5.22 Мбит/с» при режиме «Авто».
 *
 * Доля слоя пропорциональна числу его пикселей, то есть обратна квадрату
 * `scaleResolutionDownBy`. Для трёх слоёв 180p/360p/720p выходит примерно
 * 5% / 19% / 76% — верхний слой получает почти всё, а нижние остаются
 * пригодными для мелких плиток.
 *
 * У SVC список из одной записи, и она забирает бюджет целиком — ровно то,
 * что и нужно, ведь для SVC он и так считается за все слои.
 */
export function distributeBudget(scales: (number | undefined)[], budget: number): number[] {
  const weights = scales.map((scale) => 1 / Math.max(scale ?? 1, 1) ** 2);
  const total = weights.reduce((sum, w) => sum + w, 0);
  if (total <= 0) return scales.map(() => budget);
  return weights.map((w) => Math.round((budget * w) / total));
}

/**
 * Размеры нижних слоёв simulcast. Верхний слой — сама съёмка.
 *
 * Совпадают с теми, что LiveKit строит по умолчанию: библиотека считает
 * `scaleResolutionDownBy` как отношение короткой стороны съёмки к короткой
 * стороне слоя, поэтому для 720p выходит 4 / 2 / 1, а для 1080p — 6 / 3 / 1.
 */
const LOWER_LAYERS = [
  { width: 320, height: 180 },
  { width: 640, height: 360 },
] as const;

/** Во сколько раз слой мельче съёмки — от этого зависит его доля бюджета. */
function layerScales(resolution: { width: number; height: number }): number[] {
  const source = Math.min(resolution.width, resolution.height);
  return [...LOWER_LAYERS.map((l) => source / Math.min(l.width, l.height)), 1];
}

export interface LayerBudget {
  /** Потолок верхнего слоя — он же значение для `videoEncoding`. */
  top: number;
  /** Нижние слои: размер и свой потолок, для `videoSimulcastLayers`. */
  lower: { width: number; height: number; maxBitrate: number; maxFramerate: number }[];
}

/**
 * Раскладывает бюджет режима по слоям — **до** публикации дорожки.
 *
 * Раньше бюджет правился уже после публикации, через `setParameters`. Это
 * оказалось источником плавающей поломки качества: dynacast в те же первые
 * секунды включает и выключает слои тем же `setParameters`, а спецификация
 * WebRTC запрещает применять параметры, прочитанные не последним вызовом
 * `getParameters`. Кто приходил вторым — у того вызов молча падал. Если
 * проигрывал dynacast, верхний слой оставался выключенным на весь разговор,
 * и вернуть его было нечем: dynacast пишет только когда меняется список
 * нужных качеств, а он больше не менялся.
 *
 * Отсюда и «то хорошо, то плохо, и не восстанавливается»: исход зависел от
 * того, чей вызов успел первым. Теперь слои задаются сразу правильными, и
 * трогать `setParameters` при входе не нужно вовсе.
 */
export function layerBudgetFor(mode: VideoMode): LayerBudget {
  const profile = PROFILES[mode];
  const scales = layerScales(profile.resolution);
  const shares = distributeBudget(scales, profile.maxBitrate);

  return {
    top: shares[shares.length - 1],
    lower: LOWER_LAYERS.map((layer, i) => ({
      ...layer,
      maxBitrate: shares[i],
      // Мелкой плитке высокая частота кадров не нужна, а бюджет она съедает.
      maxFramerate: i === 0 ? 20 : 25,
    })),
  };
}
